import { useEffect, useRef, useState } from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import { Plus, Pencil, Trash2, Play, ShieldCheck, ShieldOff, Upload, FileUp } from 'lucide-react'
import { toast } from 'sonner'

import { testimonials as testimonialsApi } from '../api/endpoints'
import type { CustomerTestimonial } from '../api/endpoints'
import { Button } from '../components/Button'
import { DataTable } from '../components/DataTable'
import { Dialog, DialogClose, DialogContent } from '../components/Dialog'
import { FormField } from '../components/FormField'
import { Input, Textarea } from '../components/Input'
import { ImageUploader } from '../components/ImageUploader'
import { PageLoader } from '../components/Spinner'
import { parseCSV } from '../lib/parseCSV'
import { toFormData } from '../lib/formData'

/* Shape of an item in the products picker — keeps the dialog's
 * <select> payload typed without leaking the heavier AdminProduct
 * fields into this component. */
interface PickerProduct { id: string; name: string; slug: string; categoryName: string | null }

export function TestimonialsPage() {
  const [data, setData] = useState<CustomerTestimonial[]>([])
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const [name, setName] = useState('')
  const [location, setLocation] = useState('')
  const [rating, setRating] = useState('5')
  const [text, setText] = useState('')
  const [imageUrl, setImageUrl] = useState('')
  /* Newly-picked photo while the dialog is open. Null = use whatever
   * imageUrl already holds on submit. */
  const [imageFile, setImageFile] = useState<Blob | null>(null)
  const [videoUrl, setVideoUrl] = useState('')
  const [productId, setProductId] = useState<string>('')
  const [verified, setVerified] = useState('false')
  const [picker, setPicker] = useState<PickerProduct[]>([])
  const [pickerLoading, setPickerLoading] = useState(false)

  // CSV import state
  const [importOpen, setImportOpen] = useState(false)
  const [csvText, setCsvText] = useState('')
  const [csvFileName, setCsvFileName] = useState<string | null>(null)
  const [importing, setImporting] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  function reload() {
    setLoading(true)
    testimonialsApi.list()
      .then((r) => setData(r.testimonials))
      .catch((err) => toast.error('Failed to load', { description: err.message }))
      .finally(() => setLoading(false))
  }

  useEffect(reload, [])

  /* Lazy-load the product picker the first time a dialog opens.
   * Avoids dragging the catalog page-load just to view the list. */
  function loadPicker() {
    if (picker.length > 0 || pickerLoading) return
    setPickerLoading(true)
    testimonialsApi.picker()
      .then((r) => setPicker(r.products ?? []))
      .catch(() => toast.error('Failed to load products', { description: 'The product list is unavailable — try again or check your connection.' }))
      .finally(() => setPickerLoading(false))
  }

  function resetForm() {
    setName(''); setLocation(''); setRating('5'); setText(''); setImageUrl(''); setImageFile(null)
    setVideoUrl(''); setProductId(''); setVerified('false')
  }

  function openCreate() { resetForm(); loadPicker(); setCreating(true) }

  function openEdit(t: CustomerTestimonial) {
    setName(t.name); setLocation(t.location || ''); setRating(String(t.rating)); setText(t.text)
    setImageUrl(t.image || ''); setImageFile(null); setVideoUrl(t.videoUrl || '')
    setProductId(t.productId ?? ''); setVerified(String(t.verified))
    setEditing(t.id)
    loadPicker()
  }

  async function handleSave() {
    setSaving(true)
    try {
      /* Resolve the display productName from the picker so the
       * payload carries both the FK and the human-readable label.
       * Empty productId = admin explicitly cleared the picker → we
       * send productName: '' so the row updates without a product. */
      const picked = picker.find((p) => p.id === productId)
      const productName = picked ? picked.name : ''

      const payload: Record<string, unknown> = {
        name,
        location,
        rating: parseInt(rating, 10),
        text,
        imageUrl: imageUrl || null,
        videoUrl: videoUrl || null,
        productId: productId || null,
        productName,
        verified: verified === 'true',
      }

      /* If a fresh photo was picked, build FormData; otherwise send
       * JSON. The server treats imageUrl in the JSON path as the
       * "no change / explicit clear" signal. */
      if (imageFile) {
        const fd = toFormData(payload, { testimonialImage: imageFile })
        if (editing) {
          await testimonialsApi.update(editing, fd)
          toast.success('Testimonial updated')
        } else {
          await testimonialsApi.create(fd)
          toast.success('Testimonial created')
        }
      } else {
        if (editing) {
          await testimonialsApi.update(editing, payload)
          toast.success('Testimonial updated')
        } else {
          await testimonialsApi.create(payload)
          toast.success('Testimonial created')
        }
      }
      setCreating(false); setEditing(null); resetForm(); reload()
    } catch (err) {
      toast.error('Failed', { description: (err as Error).message })
    } finally { setSaving(false) }
  }

  async function handleDelete(id: string) {
    if (!confirm('Delete this testimonial?')) return
    try { await testimonialsApi.delete(id); toast.success('Deleted'); reload() }
    catch (err) { toast.error('Failed', { description: (err as Error).message }) }
  }

  function handleFilePick(file: File) {
    setCsvFileName(file.name)
    const reader = new FileReader()
    reader.onload = () => {
      setCsvText(typeof reader.result === 'string' ? reader.result : '')
    }
    reader.onerror = () => {
      toast.error('Failed to read file')
    }
    reader.readAsText(file)
  }

  async function handleImport() {
    // Use the proper CSV parser that handles quoted fields
    const rows = parseCSV(csvText)
    if (rows.length === 0) {
      toast.error('CSV must have a header row + at least one data row')
      return
    }

    const requiredHeaders = ['name', 'rating', 'text', 'productname']
    const missing = requiredHeaders.filter((h) => !(h in rows[0]))
    if (missing.length > 0) {
      toast.error(`Missing columns: ${missing.join(', ')}`)
      return
    }

    const entries = rows
      .map((row) => ({
        name: row.name,
        location: row.location || '',
        rating: Math.max(1, Math.min(5, parseInt(row.rating, 10) || 5)),
        text: row.text,
        productName: row.productname,
        imageUrl: row.imageurl || '',
        videoUrl: row.videourl || '',
        verified: row.verified === 'true',
        date: row.date || undefined,
      }))
      .filter((e) => e.name && e.text && e.productName)

    if (entries.length === 0) { toast.error('No valid rows found'); return }

    setImporting(true)
    try {
      const result = await testimonialsApi.import(entries)
      toast.success(`Imported ${result.count} testimonials`)
      setImportOpen(false)
      setCsvText('')
      setCsvFileName(null)
      reload()
    } catch (err) {
      toast.error('Import failed', { description: (err as Error).message })
    } finally { setImporting(false) }
  }

  const columns: ColumnDef<CustomerTestimonial>[] = [
    { header: 'Name', accessorKey: 'name', cell: ({ getValue }) => <span className="font-medium text-sm">{String(getValue())}</span> },
    { header: 'Product', accessorKey: 'product', cell: ({ getValue }) => <span className="text-sm text-slatey">{String(getValue())}</span> },
    { header: 'Rating', accessorKey: 'rating', cell: ({ getValue }) => <span className="text-amber font-bold">{'★'.repeat(Number(getValue()))}</span> },
    { header: 'Review', accessorKey: 'text', cell: ({ getValue }) => <p className="text-sm line-clamp-2 max-w-xs">{String(getValue())}</p> },
    { header: 'Media', cell: ({ row }) => (<div className="flex gap-1 text-xs text-slatey">{row.original.image && <span>📷</span>}{row.original.videoUrl && <span><Play className="size-3 inline" /> Video</span>}</div>) },
    { header: 'Verified', accessorKey: 'verified', cell: ({ getValue }) => getValue() ? <ShieldCheck className="size-4 text-forest" /> : <ShieldOff className="size-4 text-slatey" /> },
    { header: 'Date', accessorKey: 'date', cell: ({ getValue }) => <span className="text-xs text-slatey">{String(getValue())}</span> },
    { header: 'Actions', cell: ({ row }) => (<div className="flex gap-1"><Button variant="ghost" size="sm" onClick={() => openEdit(row.original)}><Pencil className="size-3.5" /></Button><Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleDelete(row.original.id)}><Trash2 className="size-3.5" /></Button></div>) },
  ]

  if (loading) return <PageLoader />

  return (
    <div className="space-y-6 max-w-7xl">
      <div className="flex items-center justify-between">
        <div><h1 className="text-xl font-bold text-ink">Customer Testimonials</h1><p className="text-sm text-slatey mt-1">{data.length} testimonial{data.length === 1 ? '' : 's'}</p></div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setImportOpen(true)} variant="outline"><Upload className="size-4" />Import CSV</Button>
          <Button onClick={openCreate}><Plus className="size-4" />Add Testimonial</Button>
        </div>
      </div>

      <DataTable data={data} columns={columns} emptyTitle="No testimonials yet" emptyDescription="Add customer testimonials to display." emptyAction={<Button onClick={openCreate}><Plus className="size-4" />Add</Button>} />

      {(creating || editing) && (
        <Dialog open onOpenChange={() => { setCreating(false); setEditing(null); }}>
          <DialogContent title={editing ? 'Edit Testimonial' : 'New Testimonial'}>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <FormField label="Name" required><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Rajesh Kumar" /></FormField>
                <FormField label="Location"><Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Delhi" /></FormField>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormField label="Rating (1-5)" required>
                  <select className="w-full rounded-md border border-line bg-white px-3 py-2 text-sm" value={rating} onChange={(e) => setRating(e.target.value)}>
                    {['1','2','3','4','5'].map(v => <option key={v} value={v}>{'★'.repeat(Number(v))}</option>)}
                  </select>
                </FormField>
                <FormField label="Verified">
                  <select className="w-full rounded-md border border-line bg-white px-3 py-2 text-sm" value={verified} onChange={(e) => setVerified(e.target.value)}>
                    <option value="true">Yes</option><option value="false">No</option>
                  </select>
                </FormField>
              </div>
              <FormField label="Product" required>
                <select
                  className="w-full rounded-md border border-line bg-white px-3 py-2 text-sm"
                  value={productId}
                  onChange={(e) => setProductId(e.target.value)}
                  disabled={pickerLoading}
                >
                  <option value="">{pickerLoading ? 'Loading products…' : '— Select product —'}</option>
                  {picker.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.categoryName ? `${p.name} (${p.categoryName})` : p.name}
                    </option>
                  ))}
                </select>
              </FormField>
              <FormField label="Review Text" required><Textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder="This product changed my life..." /></FormField>
              <FormField label="Photo" hint="Optional. Stored in R2 at /testimonials. Max 2048px.">
                <ImageUploader
                  value={imageUrl || null}
                  onChange={(next) => {
                    if (next instanceof Blob) {
                      setImageFile(next)
                    } else if (next === null) {
                      setImageFile(null)
                      setImageUrl('')
                    } else {
                      setImageFile(null)
                      setImageUrl(next)
                    }
                  }}
                />
              </FormField>
              <FormField label="Video URL"><Input value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)} placeholder="https://..." /></FormField>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <DialogClose asChild><Button variant="outline" onClick={() => { setCreating(false); setEditing(null); }}>Cancel</Button></DialogClose>
              <Button onClick={handleSave} disabled={saving || !name || !text || !productId}>{saving ? 'Saving…' : editing ? 'Save Changes' : 'Create'}</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {importOpen && (
        <Dialog open onOpenChange={() => { setImportOpen(false); setCsvFileName(null); setCsvText('') }}>
          <DialogContent title="Import Testimonials from CSV">
            <p className="text-sm text-slatey mb-3">
              Upload a CSV file or paste content below. Columns: name, location, rating, text, productname, imageurl, videourl, verified, date
            </p>

            <div className="mb-3">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (file) handleFilePick(file)
                  e.target.value = ''
                }}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
              >
                <FileUp className="size-4" />
                {csvFileName ? csvFileName : 'Choose CSV file'}
              </Button>
              {csvFileName ? (
                <button
                  type="button"
                  onClick={() => { setCsvFileName(null); setCsvText('') }}
                  className="ml-2 text-xs text-danger hover:underline"
                >
                  Clear file
                </button>
              ) : null}
            </div>

            <Textarea
              value={csvText}
              onChange={(e) => {
                setCsvText(e.target.value)
                setCsvFileName(e.target.value ? '(pasted)' : null)
              }}
              rows={12}
              placeholder={`name,location,rating,text,productname,imageurl,verified,date\nRajesh Kumar,Delhi,5,"Amazing product, really changed my life!",Shilajit Resin,https://...,true,2026-07-01`}
              className="font-mono text-xs"
            />
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => { setImportOpen(false); setCsvFileName(null); setCsvText('') }}>Cancel</Button>
              <Button onClick={handleImport} disabled={importing || !csvText.trim()}>
                {importing ? 'Importing…' : 'Import'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}
